/**
 * What the pointer does: axis drag, and which direction zoom and pan move.
 *
 * Both are persisted `ChartGroup` fields rather than runtime state, so they
 * belong to the plan layer and not to the shell — the shell's job is to map an
 * answer onto SciChart's modifiers, and *deciding* the answer here is what keeps
 * the four platform renderers agreeing about a chart the user configured once.
 *
 * The words are the user's, not SciChart's: `zoomAxis: "x"` says what was asked
 * for, and `EXyDirection.XDirection` is one renderer's spelling of it. Storing
 * the enum would have made the contract a SciChart.js artefact, which the .NET,
 * iOS and Android renderers would then have to translate back out of.
 */

import type { ChartGroup } from "@qufolio/model";

/** `ChartGroup["zoomAxis"]`, minus undefined. */
export type ZoomAxis = NonNullable<ChartGroup["zoomAxis"]>;

/**
 * Zoom and pan follow time by default.
 *
 * A price axis is normally `autoRange: "always"` so the visible bars fill the
 * pane. Zoom it and the next render refits it — the gesture appears to be
 * ignored, which reads as a broken chart rather than as a setting. `y` and
 * `both` are for an axis pinned to a fixed range, which is why this is a choice
 * and not a constant.
 */
export const DEFAULT_ZOOM_AXIS: ZoomAxis = "x";

/** On: it is the gesture people arrive with from every other charting tool. */
export const DEFAULT_AXIS_DRAG = true;

export interface InteractionPlan {
  /** Dragging an axis rescales it. Dragging the plot area pans either way. */
  axisDrag: boolean;
  zoomAxis: ZoomAxis;
}

/** A chart group's interaction settings, defaults filled in. */
export function resolveInteractions(
  group?: Pick<ChartGroup, "axisDrag" | "zoomAxis">,
): InteractionPlan {
  return {
    axisDrag: group?.axisDrag ?? DEFAULT_AXIS_DRAG,
    zoomAxis: group?.zoomAxis ?? DEFAULT_ZOOM_AXIS,
  };
}
